import { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import AsyncSelect from '@components/async-select/AsyncSelect';
import { impersonationUtils } from '@utils/impersonation';
import { useTheme } from '@contexts/ThemeContext';

interface ImpersonationSelectProps {
  onImpersonationStart?: () => void;
  onImpersonationStop?: () => void;
}

export default function ImpersonationSelect({ onImpersonationStart, onImpersonationStop }: ImpersonationSelectProps) {
  const { mode, colors: themeColors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState<any>(null);

  const updateImpersonationState = () => {
    setIsImpersonating(impersonationUtils.isImpersonating());
    setImpersonationInfo(impersonationUtils.getImpersonationInfo());
  };

  useEffect(() => {
    updateImpersonationState();

    // Listen for impersonation state changes
    const handleImpersonationChange = () => {
      updateImpersonationState();
    };

    window.addEventListener('impersonationChanged', handleImpersonationChange);

    return () => {
      window.removeEventListener('impersonationChanged', handleImpersonationChange);
    };
  }, []);

  const handleStartImpersonation = async (userId: string, userObject: any) => {
    if (!userId || !userObject?.origin?.id) return;

    setLoading(true);
    try {
      await impersonationUtils.startImpersonation(userObject.origin.id, 'Admin impersonation');

      notifications.show({
        title: 'Impersonation Started',
        message: `Successfully started impersonating ${userObject.origin.email}`,
        color: 'green',
      });

      onImpersonationStart?.();

      // Refresh the page to ensure all components use the new impersonated context
      window.location.reload();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to start impersonation',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      if ((await impersonationUtils.stopImpersonation()) !== 'restored') return;

      notifications.show({
        title: 'Impersonation Stopped',
        message: 'Successfully stopped impersonation',
        color: 'green',
      });

      onImpersonationStop?.();

      // Refresh the page to ensure all components use the restored original context
      window.location.reload();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to stop impersonation',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (isImpersonating && impersonationInfo) {
    const impersonatedUser = impersonationUtils.getImpersonatedUserInfo();

    return (
      <div
        className="px-3 py-2 rounded-lg"
        style={{
          backgroundColor: mode === 'light' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.15)',
          border: `1px solid ${mode === 'light' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.4)'}`,
        }}
      >
        <div className="text-xs font-medium mb-2" style={{ color: mode === 'light' ? '#7C3AED' : '#C084FC' }}>
          🎭 Impersonating: {impersonatedUser?.email || `User ID: ${impersonationInfo.targetUserId}`}
        </div>
        {impersonatedUser?.name && (
          <div className="text-xs mb-2" style={{ color: mode === 'light' ? '#8B5CF6' : '#A78BFA' }}>
            {impersonatedUser.name}
          </div>
        )}
        <button
          type="button"
          onClick={handleStopImpersonation}
          disabled={loading}
          className="w-full px-2 py-1 text-xs text-white rounded transition-colors disabled:opacity-50"
          style={{
            backgroundColor: themeColors.error,
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = mode === 'light' ? '#B91C1C' : '#DC2626';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = themeColors.error;
          }}
        >
          {loading ? 'Stopping...' : 'Stop Impersonation'}
        </button>
      </div>
    );
  }

  return (
    <div
      className="px-3 py-2 rounded-lg"
      style={{
        backgroundColor: mode === 'light' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.15)',
        border: `1px solid ${mode === 'light' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.4)'}`,
      }}
    >
      <div className="text-xs font-medium mb-2" style={{ color: mode === 'light' ? '#2563EB' : '#60A5FA' }}>
        🎭 Impersonate User
      </div>
      <AsyncSelect
        label=""
        placeholder="Search users..."
        dynamicKeyParam="query"
        path="/api/v1/users"
        additionalParam="orderBy=email&ascending=true"
        onChange={handleStartImpersonation}
        valueOnResponseKey="id"
        optionsLabelKeys={['email', 'name']}
        value=""
        size="sm"
        disabled={loading}
        // Load the user list immediately so the dropdown isn't empty until focus.
        loadOnMount
      />
    </div>
  );
}
